import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { loadFont } from './fontLoaders'
import { deriveTheme, roleNames } from '@/content/theme'
import { america, missionAt } from '@/content/missions'
import {
  attributes,
  defaultPrefs,
  PrefsContextValue,
  readShared,
  readStored,
  isSharedHash,
  durationTokens,
  CUSTOM_ROLES_KEY,
  systemTheme,
  writeStored,
  type Prefs,
  type PrefsContext,
} from './prefs'

/** Once the design changes, a shared link in the address no longer matches it. */
function dropShared() {
  if (isSharedHash(window.location.hash)) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  // A shared link's design replaces the visitor's own choices, and a
  // mission's own address, such as /cbp, chooses that mission.
  const [stored, setStored] = useState<Partial<Prefs>>(() => {
    if (typeof window === 'undefined') return readStored()
    const shared = readShared(window.location.hash)
    const addressed = missionAt(window.location.pathname)
    const mission = addressed && addressed.id !== america.id ? addressed.id : undefined
    if (shared) {
      // The design replaces the visitor's choices, but light or dark stays theirs.
      const own = readStored().theme
      const next = { ...shared, ...(own ? { theme: own } : {}), ...(mission ? { mission } : {}) }
      writeStored(next)
      return next
    }
    const saved = readStored()
    if (!mission || saved.mission === mission) return saved
    // Applying a mission brings back its own colors, as choosing it does.
    const next = { ...saved, mission }
    delete next.palette
    writeStored(next)
    return next
  })
  // Design labels start on where there is room for them, off on a phone.
  const [roomy] = useState(() => {
    try {
      return window.matchMedia('(min-width: 48rem)').matches
    } catch {
      return true
    }
  })
  const [system, setSystem] = useState<Prefs['theme']>(systemTheme)

  useEffect(() => {
    let query: MediaQueryList
    try {
      query = window.matchMedia('(prefers-color-scheme: dark)')
    } catch {
      return
    }
    const update = () => setSystem(query.matches ? 'dark' : 'light')
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  const prefs = useMemo<Prefs>(
    () => ({ ...defaultPrefs, theme: system, notes: roomy, ...stored }),
    [stored, system, roomy],
  )

  useEffect(() => {
    const root = document.documentElement
    for (const [key, attribute] of Object.entries(attributes)) {
      root.setAttribute(attribute, String(prefs[key as keyof Prefs]))
    }
    loadFont(prefs.fontDisplay)
    loadFont(prefs.fontSans)
  }, [prefs])

  // A custom palette overrides the mission's roles inline on <html>. The
  // derived roles are saved too, so theme-init.js applies them before paint.
  useEffect(() => {
    const root = document.documentElement
    const [primary, secondary, accent] = prefs.palette.split(',')
    const theme = prefs.palette ? deriveTheme({ primary, secondary, accent }) : null
    for (const name of roleNames) {
      if (theme) root.style.setProperty(`--mds-${name}`, theme[prefs.theme][name])
      else root.style.removeProperty(`--mds-${name}`)
    }
    try {
      if (theme) localStorage.setItem(CUSTOM_ROLES_KEY, JSON.stringify(theme))
      else localStorage.removeItem(CUSTOM_ROLES_KEY)
    } catch {
      // The palette still applies for this session.
    }
  }, [prefs.palette, prefs.theme])

  // Edited motion durations override the duration tokens on <html>.
  useEffect(() => {
    const root = document.documentElement
    const values = prefs.durations ? prefs.durations.split(',') : []
    durationTokens.forEach((name, i) => {
      if (values[i]) root.style.setProperty(`--mds-duration-${name}`, `${values[i]}ms`)
      else root.style.removeProperty(`--mds-duration-${name}`)
    })
  }, [prefs.durations])

  const set = useCallback<PrefsContext['set']>((key, value) => {
    dropShared()
    setStored((current) => {
      const next = { ...current, [key]: value }
      // Applying a mission brings back its own colors.
      if (key === 'mission') delete next.palette
      writeStored(next)
      return next
    })
  }, [])

  const reset = useCallback(() => {
    dropShared()
    setStored((current) => {
      const next = current.theme ? { theme: current.theme } : {}
      writeStored(next)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ prefs, themeChosen: Boolean(stored.theme), set, reset }),
    [prefs, stored.theme, set, reset],
  )
  return <PrefsContextValue.Provider value={value}>{children}</PrefsContextValue.Provider>
}
